import React, { useState } from "react";
import GeoAreaPicker from "../geo/GeoAreaPicker";
import { CoverageArea } from "../../utils/coverage";
import { ItemLocation } from "../../types/Inventory";
import { logApiError, getUserMessage } from "../../api/apiError";

interface Props {
    token: string;
    location: ItemLocation | null;
    /** Persists the location (null clears it). */
    onSave: (location: ItemLocation | null) => Promise<void>;
    disabled?: boolean;
}

/** Turns the picker chain (Country → deepest) into an ItemLocation. The
 *  label reads deepest-first ("Austin, Texas, United States"); postal codes
 *  aren't stored for item locations, so they're ignored here. */
function chainToLocation(chain: CoverageArea[], street1: string, street2: string): ItemLocation {
    const at = (level: string) => chain.find((c) => c.level === level);
    const named = chain.filter((c) => c.level !== "PostalCode");
    return {
        countryId: at("Country") ? Number(at("Country")!.id) : null,
        admin1Id: at("Admin1") ? Number(at("Admin1")!.id) : null,
        admin2Id: at("Admin2") ? Number(at("Admin2")!.id) : null,
        cityId: at("City") ? Number(at("City")!.id) : null,
        label: named.map((c) => c.name).reverse().join(", "),
        streetAddress1: street1.trim() || null,
        streetAddress2: street2.trim() || null,
    };
}

/** Where the listed thing itself is (apartment, room, house). The geo chain
 *  is what search matches on; the street address is optional and shown on
 *  the listing only — owners may leave it out until contact. */
const ItemLocationEditor: React.FC<Props> = ({ token, location, onSave, disabled }) => {
    const [chain, setChain] = useState<CoverageArea[]>([]);
    const [street1, setStreet1] = useState(location?.streetAddress1 ?? "");
    const [street2, setStreet2] = useState(location?.streetAddress2 ?? "");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const busyOrDisabled = busy || !!disabled;
    // Backend requires at least country + state.
    const chainReady = chain.some((c) => c.level === "Admin1");

    const save = async (next: ItemLocation | null) => {
        setBusy(true);
        setError(null);
        try {
            await onSave(next);
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
            {location?.label ? (
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                    <span style={{ background: "var(--surface-2, #f3f4f6)", borderRadius: "999px", padding: "0.15rem 0.6rem", fontSize: "0.85rem" }}>
                        📍 {location.label}
                    </span>
                    {(location.streetAddress1 || location.streetAddress2) && (
                        <span style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>
                            {[location.streetAddress1, location.streetAddress2].filter(Boolean).join(", ")}
                        </span>
                    )}
                    <button type="button" className="btn-sm" onClick={() => save(null)} disabled={busyOrDisabled}>
                        ✕ Clear
                    </button>
                </div>
            ) : (
                <small style={{ color: "var(--text-secondary)" }}>
                    No location yet — searchers won't find this listing by place until you set one.
                </small>
            )}

            <div style={{ border: "1px solid var(--border, #ddd)", borderRadius: "6px", padding: "0.6rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <strong style={{ fontSize: "0.85rem" }}>{location ? "Change the location" : "Set the location"}</strong>
                <GeoAreaPicker token={token} context="item" onChange={setChain} />
                <label><div>Street address (optional — shown on the listing, not searched)</div>
                    <input value={street1} onChange={(e) => setStreet1(e.target.value)} placeholder="e.g. 1200 W 6th St, Apt 4" disabled={busyOrDisabled} />
                </label>
                <label><div>Address line 2 (optional)</div>
                    <input value={street2} onChange={(e) => setStreet2(e.target.value)} disabled={busyOrDisabled} />
                </label>
                <button
                    type="button"
                    onClick={() => save(chainToLocation(chain, street1, street2))}
                    disabled={busyOrDisabled || !chainReady}
                    style={{ alignSelf: "flex-start" }}
                >
                    {busy ? "Saving…" : chainReady ? "Save location" : "Pick at least a state"}
                </button>
            </div>

            {error && <div className="error-message">{error}</div>}
        </div>
    );
};

export default ItemLocationEditor;